import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useLocation } from 'react-router'
import { operations, selectors as lmsSelectors } from 'store/lmsManager'
import { selectors as contextSelectors } from 'auth/stores/userContext'
import { selectors as licenseSelectors } from 'store/licenseManager'
import {
  CourseManagerGetter,
  CourseManagerType,
} from 'layers/content/v2/Hocs/CourseManagerGetter'
import { programs } from 'layers/content/v2/hooks/useCourseManager'
import { navigationEvent } from 'layers/navigation/store/operations'
import {
  isPreviewMode,
  transformInstructorDataToOldShape,
} from 'utils/highschoolHelpers'
import { filterUserContextForCourse } from 'utils/lms/userContextHelpers'
import {
  getInstructorDataSelectorKeyForProgram,
  getTrainingCourseTree,
  resolveK12TrainingProgramFromUrl,
} from 'utils/program/training'
import MarkCompleteSection from './component'
import {
  PROGRAM_TRAINING_LESSON_TYPE,
  SCHOOLWIDE_TRAINING_LESSON_TYPE,
  CURRICULUM_TRAINING_LESSON_TYPE,
  MARK_COMPLETE_ID,
  MARK_COMPLETE_HASH,
} from './constants'
import {
  isAdmin,
  checkIfLessonIsComplete,
  findNode,
  formatDate,
  formatSchoolName,
} from './helpers'

export const trainingProgramResolver = match => {
  const trainingProgram = resolveK12TrainingProgramFromUrl(match?.url)

  switch (trainingProgram.type) {
    case 'k5':
      return programs.elementaryTrainingOnly
    case 'middleSchool':
      return programs.middleSchoolTrainingOnly
    case 'highSchool':
      return programs.highSchool
    default:
      return programs.none
  }
}

export function MarkCompleteSectionContainer({
  // TODO: pull in props for the schoolwide training
  completionButtonLabelDefaultAdmin,
  completionButtonLabelDefaultEducator,
  completionDateDefault,
  completionDateDone,
  completionHeader,
  completionLabelDone,
  completionSchool,
  courseManager,
  courseNodeId,
  fetchInstructorProgramFlexGet,
  hsActiveSites,
  instructorDataByProgram,
  navigationEvent,
  nextStepButtonLabel,
  nextStepHeader,
  nextStepRoute,
  onMarkComplete,
  refreshInstructorProgramFlexAfterAttempt,
  userContext,
}) {
  const location = useLocation()
  const [lessonType, setLessonType] = useState('')
  const [registrationId, setRegistrationId] = useState('')
  const [leafNodeId, setLeafNodeId] = useState('')
  const [isAlreadyComplete, setIsAlreadyComplete] = useState(false)
  const [schoolName, setSchoolName] = useState(null)
  const [
    completionCommentaryComplete,
    setCompletionCommentaryComplete,
  ] = useState(null)
  const [completionCommentaryTodo, setCompletionCommentaryTodo] = useState(null)
  const [
    shoulDisplayCompletionDetails,
    setShoulDisplayCompletionDetails,
  ] = useState(false)
  const trainingProgram = resolveK12TrainingProgramFromUrl(location.pathname)
  const instructorDataSelectorKey = getInstructorDataSelectorKeyForProgram(
    trainingProgram.type,
  )
  const instructorData =
    instructorDataByProgram?.[instructorDataSelectorKey] || null
  const trainingCourseTreeName = trainingProgram.courseTreeName

  useEffect(() => {
    const isK8Training =
      trainingProgram.type === 'k5' || trainingProgram.type === 'middleSchool'

    if (isK8Training && !isPreviewMode()) {
      fetchInstructorProgramFlexGet()
    }
  }, [fetchInstructorProgramFlexGet, trainingProgram.type])

  useEffect(() => {
    if (lessonType && lessonType !== SCHOOLWIDE_TRAINING_LESSON_TYPE) {
      setShoulDisplayCompletionDetails(false)
      return
    }

    setShoulDisplayCompletionDetails(true)

    const currentSite = hsActiveSites.find(
      site => site?.id === userContext.hsActiveSiteId,
    )

    let completionTodo = ''

    setSchoolName(currentSite?.name)
    if (!!completionSchool && schoolName) {
      completionTodo = `${completionSchool} ${formatSchoolName(schoolName)}\n`
    }

    if (completionDateDefault) {
      completionTodo += completionDateDefault
    }

    setCompletionCommentaryTodo(completionTodo)
  }, [lessonType, hsActiveSites, userContext])

  useEffect(() => {
    // fake the scroll to hash because we're using a navigation event, not a real a href attribute:
    if (window?.location?.hash === MARK_COMPLETE_HASH) {
      setTimeout(() => {
        const markCompleteSection = document.getElementById(MARK_COMPLETE_ID)
        markCompleteSection?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 100) // add a 100ms delay to allow the page to fully render before scrolling
    }
  }, [])

  useEffect(() => {
    function updateLessonData(instructorData) {
      setRegistrationId(instructorData?.registration_id)

      const { isComplete, date } = checkIfLessonIsComplete(
        instructorData?.lesson_nodes || [],
        courseNodeId,
      )

      setIsAlreadyComplete(isComplete)

      let completionComentary = ''

      if (!!completionSchool && !!schoolName) {
        completionComentary = `${completionSchool} ${formatSchoolName(
          schoolName,
        )}\n`
      }

      if (!!completionDateDone && !!date) {
        completionComentary += `${completionDateDone} ${formatDate(date)}.`
      }

      setCompletionCommentaryComplete(completionComentary)
    }

    if (!courseNodeId) {
      console.warn(
        'courseNodeId must be set in order to use MarkCompleteSectionContainer',
      )
      return
    }

    const trainingCourseTree = getTrainingCourseTree(courseManager, {
      courseTreeName: trainingCourseTreeName,
    })
    if (!trainingCourseTree) {
      return
    }
    if (!instructorData) {
      return
    }
    const transformedInstructorData = transformInstructorDataToOldShape(
      instructorData,
    )

    const courseTreeNode = findNode(trainingCourseTree, courseNodeId)
    if (!courseTreeNode) {
      console.warn(
        `courseNodeId ${courseNodeId} not found in courseManager.courseTrees.${trainingCourseTreeName}`,
      )
      return
    }
    setLessonType(courseTreeNode.type)
    setLeafNodeId(courseTreeNode.leafNodeId)
    switch (courseTreeNode.type) {
      // these two training types use the same registration_id:
      case PROGRAM_TRAINING_LESSON_TYPE:
      case CURRICULUM_TRAINING_LESSON_TYPE:
        updateLessonData(transformedInstructorData?.educator)
        break
      case SCHOOLWIDE_TRAINING_LESSON_TYPE:
        if (schoolName) {
          updateLessonData(transformedInstructorData?.site)
        }
        break
    }
  }, [
    courseManager,
    courseNodeId,
    instructorData,
    schoolName,
    trainingCourseTreeName,
  ])

  async function handleToggleComplete(isComplete) {
    if (isComplete) {
      const filteredUserContext = filterUserContextForCourse(
        userContext,
        trainingCourseTreeName,
        trainingProgram.programKey,
      )

      const payload = {
        registrationId,
        leafNodeId: leafNodeId || courseNodeId,
        userContextB64: btoa(JSON.stringify(filteredUserContext)),
      }
      // TODO: we may want to support other lessonTypes in this component in the future:
      if (
        lessonType === PROGRAM_TRAINING_LESSON_TYPE ||
        lessonType === SCHOOLWIDE_TRAINING_LESSON_TYPE ||
        lessonType === CURRICULUM_TRAINING_LESSON_TYPE
      ) {
        payload.progress = 1
      }
      await onMarkComplete(payload)
      await refreshInstructorProgramFlexAfterAttempt(userContext)
    }
  }

  function handleClickNext() {
    navigationEvent(nextStepRoute)
  }

  // should only be able to mark button complete in instance mode
  if (isPreviewMode()) {
    return null
  }

  return (
    <MarkCompleteSection
      buttonText={
        isAdmin(userContext)
          ? completionButtonLabelDefaultAdmin
          : completionButtonLabelDefaultEducator
      }
      completeText={completionLabelDone}
      completionCommentaryComplete={completionCommentaryComplete}
      completionCommentaryTodo={completionCommentaryTodo}
      handleClickNext={handleClickNext}
      handleToggleComplete={handleToggleComplete}
      hasPermissionsToComplete={
        // only admins can mark schoolwide training as complete
        // all other trainings, anyone can mark complete
        lessonType !== SCHOOLWIDE_TRAINING_LESSON_TYPE || isAdmin(userContext)
      }
      id={MARK_COMPLETE_ID}
      isAlreadyComplete={isAlreadyComplete}
      nextSectionButtonText={nextStepButtonLabel}
      nextSectionPromptText={nextStepHeader}
      promptText={completionHeader}
      shoulDisplayCompletionDetails={shoulDisplayCompletionDetails}
    />
  )
}

const mapStateToProps = state => {
  const activeSites = licenseSelectors?.selectActiveSites(state) || []
  let hsActiveSites = []

  if (Array.isArray(activeSites)) {
    hsActiveSites = activeSites.filter(sites =>
      sites?.licenses?.some(license =>
        license?.product?.programCodes?.includes('HSP'),
      ),
    )
  }

  return {
    hsActiveSites,
    instructorDataByProgram: {
      hs: lmsSelectors.selectHsInstructorData(state),
      k5: lmsSelectors.selectk5InstructorData(state),
      middleSchool: lmsSelectors.selectMiddleSchoolInstructorData(state),
    },
    // TODO LEARN-20166 Update hs to use useUserCOntext

    userContext: contextSelectors.selectProgramContext(state),
  }
}

const mapDispatchToProps = {
  fetchInstructorProgramFlexGet: operations.fetchInstructorProgramFlexGet,
  onMarkComplete: operations.makeAttemptV2,
  navigationEvent,
  refreshInstructorProgramFlexAfterAttempt:
    operations.refreshInstructorProgramFlexAfterAttempt,
}

MarkCompleteSectionContainer.propTypes = {
  completionButtonLabelDefaultAdmin: PropTypes.string,
  completionButtonLabelDefaultEducator: PropTypes.string,
  completionDateDefault: PropTypes.string,
  completionDateDone: PropTypes.string,
  completionHeader: PropTypes.string,
  completionLabelDone: PropTypes.string,
  completionSchool: PropTypes.string,
  courseManager: CourseManagerType,
  courseNodeId: PropTypes.string,
  fetchInstructorProgramFlexGet: PropTypes.func,
  hsActiveSites: PropTypes.array,
  instructorDataByProgram: PropTypes.shape({
    hs: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    k5: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    middleSchool: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  }),
  navigationEvent: PropTypes.func,
  nextStepButtonLabel: PropTypes.string,
  nextStepHeader: PropTypes.string,
  nextStepRoute: PropTypes.string,
  onMarkComplete: PropTypes.func,
  refreshInstructorProgramFlexAfterAttempt: PropTypes.func,
  userContext: PropTypes.object,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  // The flag-on variant is what renders once the k12 core curriculum-service
  // flag is on (see components/MarkCompleteSection/index.js), and it resolves
  // leaf node ids from the authored tree via useMarkCompleteV3. This variant is
  // the pre-curriculum-service fallback, so it stays on the legacy course
  // manager and the audienced structure.
  CourseManagerGetter(MarkCompleteSectionContainer, {
    programResolver: trainingProgramResolver,
  }),
)
